// Compliance Document Center helpers — Firebase Storage + Firestore.
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  addDoc, collection, serverTimestamp, updateDoc, doc, deleteDoc
} from "firebase/firestore";
import { db, storage } from "./firebase";

const MAX_BYTES = 15 * 1024 * 1024; // 15MB safety cap

// Upload a compliance document file to Storage and create a Firestore record.
export async function uploadComplianceDoc({ file, category, docKey, label, ownerUid, ownerName, uploadedBy, uploadedByRole, status }) {
  if (!file) throw new Error("No file selected.");
  if (file.size > MAX_BYTES) throw new Error("File is too large (max 15MB).");
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const owner = ownerUid || "shared";
  const path = `compliance/${category}/${owner}/${docKey}-${Date.now()}-${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  const fileUrl = await getDownloadURL(storageRef);
  const record = {
    category,
    docKey,
    label,
    ownerUid: ownerUid || null,
    ownerName: ownerName || null,
    fileUrl,
    fileName: file.name,
    storagePath: path,
    status: status || "pending",
    expiresAt: null,
    notes: "",
    uploadedBy: uploadedBy || null,
    uploadedByRole: uploadedByRole || null,
    createdAt: serverTimestamp()
  };
  const docRef = await addDoc(collection(db, "compliance_documents"), record);
  return { id: docRef.id, ...record };
}

// Create a non-file record (e.g. SOP acknowledgement).
export async function acknowledgeDoc({ docKey, label, ownerUid, ownerName, uploadedBy }) {
  const record = {
    category: "driver",
    docKey,
    label,
    ownerUid: ownerUid || null,
    ownerName: ownerName || null,
    fileUrl: null,
    fileName: null,
    storagePath: null,
    status: "acknowledged",
    expiresAt: null,
    notes: "",
    uploadedBy: uploadedBy || null,
    uploadedByRole: "driver",
    createdAt: serverTimestamp()
  };
  const docRef = await addDoc(collection(db, "compliance_documents"), record);
  return { id: docRef.id, ...record };
}

export async function updateComplianceDoc(id, patch) {
  await updateDoc(doc(db, "compliance_documents", id), patch);
}

export async function deleteComplianceDoc(id) {
  await deleteDoc(doc(db, "compliance_documents", id));
}

// Expiry helpers
export function expiryState(expiresAt) {
  if (!expiresAt) return { label: "No expiry", cls: "text-slate-500", expired: false, soon: false };
  const d = new Date(expiresAt);
  const now = new Date();
  const days = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, cls: "text-red-600", expired: true, soon: false };
  if (days <= 30) return { label: `Expires in ${days}d`, cls: "text-amber-600", expired: false, soon: true };
  return { label: `Valid · expires ${d.toLocaleDateString()}`, cls: "text-emerald-600", expired: false, soon: false };
}
